"use strict";
var _StateFlow_initializedManagers, _Instance_stack, _a;
Object.defineProperty(exports, "__esModule", { value: true });
exports.Instance = exports.StateFlow = void 0;
const tslib_1 = require("tslib");
const events_1 = require("events");
const es6_1 = tslib_1.__importDefault(require("fast-deep-equal/es6"));
const group_1 = require("./group");
const result_1 = require("./result");
const signals_1 = require("./signals");
const state_type_1 = require("./state-type");
const symbols_1 = require("./symbols");
const utils_1 = require("./utils");
const disposable_stack_1 = tslib_1.__importDefault(require("core-js-pure/actual/disposable-stack"));
function eventKey(kind, group, state) {
    if (state instanceof state_type_1.StateInstance) {
        return `${kind}:${String(group)}:${(0, utils_1.getStateName)(state)}`;
    }
    return `${kind}:${String(group)}:${state.stateName}`;
}
function printLog(signal, result, params, logs, transition = false) {
    if (!utils_1.DEBUG_MODE) {
        return;
    }
    if (transition) {
        console.groupCollapsed(`[SIGNAL/ASYNC] ${String(signal)} - ${result}`);
    }
    else {
        console.groupCollapsed(`[SIGNAL] ${String(signal)} - ${result}`);
    }
    console.log("Params:", params);
    for (const [state, val] of Object.entries(logs)) {
        for (const args of val) {
            console.log(...args);
        }
    }
    console.groupEnd();
}
let curManager = "";
/**
 * Represents a state flow which manages the state of groups and signals.
 *
 * @template Groups - The type of groups.
 * @template Signals - The type of signals.
 */
class StateFlow {
    // readonly initialState: Record<keyof Groups, () => StateType<any>> = {} as Record<keyof Groups, () => StateType<any>>;
    constructor() {
        this.registeredGroups = [];
        _StateFlow_initializedManagers.set(this, new WeakSet());
    }
    registerGroup(name) {
        const group = new group_1.Group(name);
        if (this.registeredGroups.some((g) => g.name === group.name)) {
            console.error(new Error(`Group ${String(group.name)} is already registered`));
            return group;
        }
        this.registeredGroups.push(group);
        // this.initialState[name] = init;
        return group;
    }
    initialize(...managers) {
        const sf = new Instance(this);
        for (const group of this.registeredGroups) {
            if (group.initFactory != null) {
                sf[symbols_1.applySym](group.name, group.initFactory());
            }
        }
        for (const manager of managers) {
            curManager = "displayName" in manager.constructor
                ? String(manager.constructor.displayName)
                : manager.constructor.name;
            manager.initStateFlow(sf);
        }
        curManager = "";
        // must be immutable
        sf[symbols_1.isInitializedSym] = true;
        if (utils_1.DEBUG_MODE) {
            for (const group of this.registeredGroups) {
                for (const state of group.registeredStates) {
                    console.debug(`Registered state ${group.name}(${state.stateName}) `
                        + `with ${Object.keys(group.registeredHandlers[state.stateName]).length} handlers`);
                }
            }
        }
        // Object.freeze(sf);
        return sf;
    }
}
exports.StateFlow = StateFlow;
_StateFlow_initializedManagers = new WeakMap();
/**
 * Instance of specific state flow.
 */
class Instance {
    constructor(smt) {
        this[_a] = false;
        _Instance_stack.set(this, new disposable_stack_1.default());
        this[symbols_1.instanceSym] = smt;
        this[symbols_1.emitterSym] = tslib_1.__classPrivateFieldGet(this, _Instance_stack, "f").adopt(new events_1.EventEmitter(), (emitter) => {
            emitter.removeAllListeners();
        });
    }
    dispatch(signalType, params, handler) {
        if (!this[symbols_1.isInitializedSym]) {
            throw new Error("State flow is not initialized");
        }
        const collector = new utils_1.Collector(result_1.Result.ok());
        let result = result_1.Result.ignore();
        try {
            result = this.prepareChangelog(signalType, params, collector);
            if (!result.ok || result.data == null) {
                return result.withContext(signalType);
            }
            const [snapshot, changelog] = result.data;
            this.processExitHandlers(changelog, snapshot, collector);
            result = collector.merge();
            if (result.hasError) {
                return result.withContext(signalType);
            }
            if (result.kind === result_1.ResultKind.InTransition) {
                return result_1.Result.error(new Error("async handlers are not allowed in sync dispatch")).withContext(signalType);
            }
            if (handler != null) {
                const res = handler(snapshot, this);
                collector === null || collector === void 0 ? void 0 : collector.log("LOCAL", `\tonChange: local handler => ${String(res)}`);
                collector === null || collector === void 0 ? void 0 : collector.push(res);
                result = collector.merge();
            }
            if (result.hasError) {
                return result.withContext(signalType);
            }
            if (result.kind === result_1.ResultKind.InTransition) {
                return result_1.Result.error(new Error("async handlers are not allowed in sync dispatch")).withContext(signalType);
            }
            this.processChangesHandlers(changelog, snapshot, collector);
            result = collector.merge();
            if (result.hasError) {
                return result.withContext(signalType);
            }
            if (result.kind === result_1.ResultKind.InTransition) {
                return result_1.Result.error(new Error("async handlers are not allowed in sync dispatch")).withContext(signalType);
            }
            this.processEnterHandlers(changelog, snapshot, collector);
            result = collector.merge();
            if (result.hasError) {
                return result.withContext(signalType);
            }
            if (result.kind === result_1.ResultKind.InTransition) {
                return result_1.Result.error(new Error("async handlers are not allowed in sync dispatch")).withContext(signalType);
            }
            for (const [group, state] of changelog) {
                this[symbols_1.applySym](group, state);
            }
            this.processCommitEvents(changelog, snapshot);
            return result.withContext(signalType);
        }
        finally {
            this[symbols_1.clearSym]();
            printLog(signalType, result, params, collector.logs);
        }
    }
    async dispatchAsync(signalType, params) {
        if (!this[symbols_1.isInitializedSym]) {
            throw new Error("State flow is not initialized");
        }
        const collector = new utils_1.Collector(result_1.Result.ok());
        let result = result_1.Result.ignore();
        try {
            result = this.prepareChangelog(signalType, params, collector);
            if (!result.ok || result.data == null) {
                return result.withContext(signalType);
            }
            const [snapshot, changelog] = result.data;
            this.processExitHandlers(changelog, snapshot, collector);
            result = await collector.merge().ready;
            if (result.hasError) {
                return result.withContext(signalType);
            }
            this.processChangesHandlers(changelog, snapshot, collector);
            result = await collector.merge().ready;
            if (result.hasError) {
                return result.withContext(signalType);
            }
            this.processEnterHandlers(changelog, snapshot, collector);
            result = await collector.merge().ready;
            if (result.hasError) {
                return result.withContext(signalType);
            }
            for (const [group, state] of changelog) {
                this[symbols_1.applySym](group, state);
            }
            this.processCommitEvents(changelog, snapshot);
            return result.withContext(signalType);
        }
        finally {
            this[symbols_1.clearSym]();
            printLog(signalType, result, params, collector.logs, true);
        }
    }
    /**
     * Applies a new state to the state machine for the specified group.
     * Notifies all watchers about the state change.
     */
    [(_Instance_stack = new WeakMap(), _a = symbols_1.isInitializedSym, symbols_1.applySym)](group, state) {
        const self = this;
        self[group] = state;
    }
    [symbols_1.clearSym]() {
        Object.values(this).forEach((s) => s[symbols_1.isTransitioningSym] = false);
    }
    handleEnter(state, cb) {
        this.feedbackWatch(state, cb, "onEnter");
    }
    handleChanges(state, cb) {
        this.feedbackWatch(state, cb, "onChanges");
    }
    handleExit(state, cb) {
        this.feedbackWatch(state, cb, "onExit");
    }
    onCommit(state, cb) {
        this.notifyWatch(state, cb);
    }
    [Symbol.dispose]() {
        tslib_1.__classPrivateFieldGet(this, _Instance_stack, "f").dispose();
        this[symbols_1.emitterSym].removeAllListeners();
        this[symbols_1.isInitializedSym] = false;
    }
    isStateChanged(group, state) {
        if (state == null) {
            return false;
        }
        const self = this;
        const oldState = self[group];
        // initial state
        if (oldState == null) {
            return true;
        }
        const oldStateName = (0, utils_1.getStateName)(oldState);
        const newStateName = (0, utils_1.getStateName)(state);
        return !(oldStateName === newStateName && (0, es6_1.default)(self[group], state));
    }
    processEnterHandlers(changelog, snapshot, collector) {
        const self = this;
        // handle exit states
        for (const [group, state] of changelog) {
            if ((0, utils_1.getStateName)(self[group]) !== (0, utils_1.getStateName)(snapshot[group])) {
                const key = eventKey("onEnter", group, snapshot[group]);
                this[symbols_1.emitterSym].emit(key, snapshot, collector);
            }
        }
    }
    processChangesHandlers(changelog, snapshot, collector) {
        const self = this;
        // handle exit states
        for (const [group, state] of changelog) {
            if ((0, utils_1.getStateName)(self[group]) === (0, utils_1.getStateName)(snapshot[group])) {
                const key = eventKey("onChanges", group, snapshot[group]);
                this[symbols_1.emitterSym].emit(key, snapshot, collector);
            }
        }
    }
    processExitHandlers(changelog, snapshot, collector) {
        const self = this;
        // handle exit states
        for (const [group, state] of changelog) {
            if ((0, utils_1.getStateName)(self[group]) !== (0, utils_1.getStateName)(snapshot[group])) {
                const key = eventKey("onExit", group, self[group]);
                this[symbols_1.emitterSym].emit(key, snapshot, collector);
            }
        }
    }
    processCommitEvents(changelog, snapshot, collector) {
        const self = this;
        // handle exit states
        for (const [group, state] of changelog) {
            const key = eventKey("onCommit", group, self[group]);
            this[symbols_1.emitterSym].emit(key, snapshot, collector);
        }
    }
    /**
     * Dispatches a signal to the state machine and returns a result of the signal handling.
     * If no handlers were found, the result will be `SignalResult.Ignored`.
     * If at least one of handlers was failed, the result will be `SignalResult.Error`.
     * Otherwise, the result will be `SignalResult.Handled`.
     */
    prepareChangelog(signalType, params, collector) {
        const signal = signalType instanceof signals_1.SignalType ? signalType.name : signalType;
        const changelog = [];
        const snapshot = {};
        // handle state changes but don't apply them yet
        for (const group of this[symbols_1.instanceSym].registeredGroups) {
            const state = this.get(group.name);
            snapshot[group.name] = state;
            // check if the state handles the signal
            if (group.accepts(state, signal)) {
                let result = group.handle(this, signal, params);
                switch (result.kind) {
                    case result_1.ResultKind.OK:
                        if (this.isStateChanged(group.name, result.data)) {
                            // got a really new state, so should be handled and applied
                            // unless it's in transition already
                            if (state[symbols_1.isTransitioningSym]) {
                                return result_1.Result.error(Error(`Cannot handle '${String(signalType)}' signal: '${group.name}' is in transition`));
                            }
                            // mark the state as in transition
                            // we don't have to reset the flag because the new state
                            // will be applied without this flag!
                            state[symbols_1.isTransitioningSym] = true;
                            changelog.push([group.name, result.data]);
                            snapshot[group.name] = result.data;
                            collector.log(group.name, `State ${group.name}: ${(0, utils_1.getStateName)(state)} => ${(0, utils_1.getStateName)(result.data)}`, [{ ...result.data }]);
                        }
                        break;
                    case result_1.ResultKind.InTransition:
                        // stop handling the signal immediately
                        return result_1.Result.error(new Error("Transition is not allowed in signal handlers"));
                    case result_1.ResultKind.Rejected:
                    case result_1.ResultKind.Error:
                        // stop handling the signal immediately too
                        return result;
                }
            }
        }
        if (changelog.length === 0) {
            return result_1.Result.ignore("no changes");
        }
        return result_1.Result.ok([snapshot, changelog]);
    }
    get(stateName) {
        if (!(stateName in this)) {
            throw new Error(`State ${stateName} not found`);
        }
        return this[stateName];
    }
    clone() {
        const self = this;
        return this[symbols_1.instanceSym].registeredGroups.reduce((acc, group) => {
            const k = group.name;
            acc[k] = { ...self[k] };
            return acc;
        }, {});
    }
    /**
     * Watches for state changes of the specified group.
     * Returns a function to stop watching.
     */
    feedbackWatch(state, cb, kind) {
        if (this[symbols_1.isInitializedSym]) {
            throw new Error("Cannot add handlers after initialization");
        }
        const wrapper = (snapshot, collector) => {
            try {
                const res = cb(snapshot, this);
                collector === null || collector === void 0 ? void 0 : collector.log(String(state.groupName), `\t${kind}: ${cb.name}() => ${String(res)}`);
                collector === null || collector === void 0 ? void 0 : collector.push(res);
            }
            catch (err) {
                collector === null || collector === void 0 ? void 0 : collector.log(String(state.groupName), `\t${kind}: ${cb.name}() => Error: ${String(err)}`);
                collector === null || collector === void 0 ? void 0 : collector.push(err);
            }
        };
        this[symbols_1.emitterSym].on(eventKey(kind, String(state.groupName), state), wrapper);
    }
    /**
     * Watches for state changes of the specified group.
     * Returns a function to stop watching.
     */
    notifyWatch(state, cb) {
        const wrapper = (snapshot, collector) => {
            try {
                cb(snapshot, this);
            }
            catch (err) {
                console.error(`Error in ${String(state)} "onCommit" callback: ${err}`, err);
            }
        };
        this[symbols_1.emitterSym].on(eventKey("onCommit", String(state.groupName), state), wrapper);
    }
}
exports.Instance = Instance;
//# sourceMappingURL=state-flow.js.map